import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, updateData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Button, TextField, MenuItem, Paper, Chip, Alert,
  Snackbar, Checkbox, CircularProgress, InputAdornment, Collapse, IconButton
} from '@mui/material';
import { SwapHoriz, Search, ExpandMore, ExpandLess } from '@mui/icons-material';

const PAGE_SIZE = 50;

const GSTItemBulkUpdate = () => {
  useSessionValidation();
  const { companyId, token, username } = useAuth();
  const { logAudit } = useAuditLog();

  const [gstRates, setGstRates] = useState([]);
  const [oldGstId, setOldGstId] = useState('');
  const [newGstId, setNewGstId] = useState('');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [expandedCats, setExpandedCats] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [catPages, setCatPages] = useState({});

  useEffect(() => {
    if (!companyId || !token) return;
    setLoading(true);
    fetchData('GSTRateApi', `/${companyId}`, token)
      .then(res => {
        const data = res.response_data || res || [];
        setGstRates(Array.isArray(data) ? data.filter(r => r.IsActive !== false) : []);
      })
      .catch(() => setGstRates([]))
      .finally(() => setLoading(false));
  }, [companyId, token]);

  useEffect(() => {
    if (!oldGstId || !token) {
      setCategories([]); setItems([]); setSelectedItems(new Set()); setExpandedCats({}); setCatPages({});
      return;
    }
    setFetching(true);
    setCategories([]); setItems([]); setSelectedItems(new Set()); setExpandedCats({}); setCatPages({});
    fetchData('GSTRateApi', `/ratewisectegory/${oldGstId}`, token)
      .then(res => {
        const responseData = res.response_data || res || [];
        const data = Array.isArray(responseData) ? responseData[0] || {} : responseData;
        const cats = data.Category || data.category || [];
        const itms = data.Item || data.item || [];
        setCategories(Array.isArray(cats) ? cats : []);
        setItems(Array.isArray(itms) ? itms : []);
      })
      .catch(() => { setCategories([]); setItems([]); })
      .finally(() => setFetching(false));
  }, [oldGstId, token]);

  // Group items by categoryId (memoized for performance)
  const itemsByCategory = useMemo(() => {
    const map = {};
    items.forEach(i => {
      const catId = i.CategoryId;
      if (!map[catId]) map[catId] = [];
      map[catId].push(i);
    });
    return map;
  }, [items]);

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;
    const q = searchTerm.toLowerCase();
    return categories.filter(c =>
      (c.Name || '').toLowerCase().includes(q) ||
      (itemsByCategory[c.Id] || []).some(i => (i.Name || '').toLowerCase().includes(q) || (i.Code || '').includes(q))
    );
  }, [categories, searchTerm, itemsByCategory]);

  const toggleCategory = useCallback((catId) => {
    setExpandedCats(prev => ({ ...prev, [catId]: !prev[catId] }));
  }, []);

  const handleSelectItem = useCallback((id, checked) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const handleSelectCategory = useCallback((catId, checked) => {
    const catItems = itemsByCategory[catId] || [];
    setSelectedItems(prev => {
      const next = new Set(prev);
      catItems.forEach(i => checked ? next.add(i.Id) : next.delete(i.Id));
      return next;
    });
  }, [itemsByCategory]);

  const handleSelectAll = useCallback((checked) => {
    setSelectedItems(checked ? new Set(items.map(i => i.Id)) : new Set());
  }, [items]);

  const isCatSelected = useCallback((catId) => {
    const catItems = itemsByCategory[catId] || [];
    return catItems.length > 0 && catItems.every(i => selectedItems.has(i.Id));
  }, [itemsByCategory, selectedItems]);

  const isCatIndeterminate = useCallback((catId) => {
    const catItems = itemsByCategory[catId] || [];
    if (catItems.length === 0) return false;
    const count = catItems.filter(i => selectedItems.has(i.Id)).length;
    return count > 0 && count < catItems.length;
  }, [itemsByCategory, selectedItems]);

  const handleUpdate = async () => {
    if (!newGstId || selectedItems.size === 0) { setError('Select a new GST rate and at least one item'); return; }
    const newRate = gstRates.find(g => String(g.Id) === String(newGstId));
    if (!newRate) return;

    setUpdating(true);
    try {
      const payload = [...selectedItems].map(itemId => {
        const item = items.find(i => i.Id === itemId);
        if (!item) return null;
        return { ...item, GSTRate: parseFloat(newRate.Rate), GSTRateId: parseInt(newGstId), OldGSTRate: item.GSTRate, GSTRateLastUpdatedDate: new Date().toISOString(), GSTRateLastUpdatedUserName: username || '', Company: null, UOM: null, Category: null, Brand: null, Master: null };
      }).filter(Boolean);

      await updateData('GSTRateApi', 0, payload, token);
      setSuccess(`Updated ${payload.length} item(s) to ${newRate.Rate}%`);
      setOldGstId(''); setNewGstId(''); setCategories([]); setItems([]); setSelectedItems(new Set()); setExpandedCats({});
    } catch (err) {
      console.error('Update failed:', err);
      setError('Update failed. Try again.');
    } finally { setUpdating(false); }
  };

  const oldRate = gstRates.find(g => String(g.Id) === String(oldGstId));
  const newRate = gstRates.find(g => String(g.Id) === String(newGstId));

  return (
    <div style={{ padding: '8px 12px', minHeight: 'calc(100vh - 48px)', background: '#f0f4f8' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <SwapHoriz sx={{ fontSize: 18, color: '#2C3E6E' }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', letterSpacing: 0.3 }}>GST ITEM BULK UPDATE</span>
        </div>
        {items.length > 0 && <Chip label={`${selectedItems.size}/${items.length}`} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#1d4ed8' }} />}
      </div>

      {/* Controls */}
      <div style={{ background: '#fff', borderRadius: 6, padding: '8px 10px', marginBottom: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField select label="Old GST" size="small" value={oldGstId}
          onChange={(e) => { setOldGstId(e.target.value); setNewGstId(''); setSearchTerm(''); }}
          sx={{ width: 140, '& .MuiInputBase-root': { height: 30, fontSize: 11 }, '& .MuiInputLabel-root': { fontSize: 10 } }}>
          <MenuItem value="" sx={{ fontSize: 11 }}>--</MenuItem>
          {gstRates.map(g => <MenuItem key={g.Id} value={g.Id} sx={{ fontSize: 11 }}>{g.Name || `${g.Rate}%`}</MenuItem>)}
        </TextField>

        <span style={{ fontSize: 14, color: '#94a3b8' }}>→</span>

        <TextField select label="New GST" size="small" value={newGstId} disabled={!oldGstId}
          onChange={(e) => setNewGstId(e.target.value)}
          sx={{ width: 140, '& .MuiInputBase-root': { height: 30, fontSize: 11 }, '& .MuiInputLabel-root': { fontSize: 10 } }}>
          <MenuItem value="" sx={{ fontSize: 11 }}>--</MenuItem>
          {gstRates.map(g => <MenuItem key={g.Id} value={g.Id} sx={{ fontSize: 11 }}>{g.Name || `${g.Rate}%`}</MenuItem>)}
        </TextField>

        {categories.length > 0 && (
          <TextField size="small" placeholder="Search..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14, color: '#94a3b8' }} /></InputAdornment> }}
            sx={{ width: 150, '& .MuiInputBase-root': { height: 30, fontSize: 11 } }} />
        )}

        {items.length > 0 && (
          <Checkbox size="small" sx={{ p: 0.2 }}
            checked={selectedItems.size === items.length && items.length > 0}
            indeterminate={selectedItems.size > 0 && selectedItems.size < items.length}
            onChange={(e) => handleSelectAll(e.target.checked)} />
        )}

        {oldRate && newRate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ background: '#fee2e2', color: '#991b1b', padding: '1px 6px', borderRadius: 8, fontSize: 9, fontWeight: 700 }}>{oldRate.Rate}%</span>
            <span style={{ fontSize: 10, color: '#6b7280' }}>→</span>
            <span style={{ background: '#d1fae5', color: '#065f46', padding: '1px 6px', borderRadius: 8, fontSize: 9, fontWeight: 700 }}>{newRate.Rate}%</span>
          </div>
        )}

        <Button variant="contained" size="small" onClick={handleUpdate}
          disabled={updating || !newGstId || selectedItems.size === 0}
          sx={{ bgcolor: '#2C3E6E', '&:hover': { bgcolor: '#1e2d4f' }, textTransform: 'none', fontWeight: 600, fontSize: 10, height: 28, px: 1.5, ml: 'auto', minWidth: 0 }}>
          {updating && <CircularProgress size={12} sx={{ color: '#fff', mr: 0.5 }} />}
          Update {selectedItems.size}
        </Button>
      </div>

      {/* Category List */}
      {(loading || fetching) ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><CircularProgress size={24} /></div>
      ) : filteredCategories.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}>
          {filteredCategories.map(cat => {
            const catId = cat.Id;
            const allCatItems = itemsByCategory[catId] || [];
            // When searching, show only matching items (unless the category name itself matched)
            const catItems = searchTerm
              ? (() => {
                  const q = searchTerm.toLowerCase();
                  const catNameMatches = (cat.Name || '').toLowerCase().includes(q);
                  if (catNameMatches) return allCatItems;
                  return allCatItems.filter(i => (i.Name || '').toLowerCase().includes(q) || (i.Code || '').toLowerCase().includes(q));
                })()
              : allCatItems;
            const expanded = expandedCats[catId];
            const selCount = catItems.filter(i => selectedItems.has(i.Id)).length;
            const page = catPages[catId] || 1;
            const pagedItems = catItems.slice(0, page * PAGE_SIZE);

            return (
              <div key={catId} style={{ background: '#fff', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', cursor: 'pointer', gap: 6, borderLeft: expanded ? '3px solid #2C3E6E' : '3px solid transparent' }}
                  onClick={() => toggleCategory(catId)}>
                  <Checkbox size="small" sx={{ p: 0.2 }}
                    checked={isCatSelected(catId)} indeterminate={isCatIndeterminate(catId)}
                    onChange={(e) => { e.stopPropagation(); handleSelectCategory(catId, e.target.checked); }}
                    onClick={(e) => e.stopPropagation()} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#1e293b', flex: 1 }}>{cat.Name}</span>
                  <span style={{ fontSize: 9, color: '#6b7280', background: '#f1f5f9', padding: '1px 5px', borderRadius: 6 }}>{catItems.length}</span>
                  {selCount > 0 && <span style={{ fontSize: 9, color: '#1d4ed8', background: '#dbeafe', padding: '1px 5px', borderRadius: 6 }}>{selCount}</span>}
                  <IconButton size="small" sx={{ p: 0.2 }}>
                    {expanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                  </IconButton>
                </div>

                <Collapse in={expanded} timeout={150} unmountOnExit>
                  <div style={{ borderTop: '1px solid #f1f5f9' }}>
                    {catItems.length === 0 ? (
                      <div style={{ padding: 8, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>No items</div>
                    ) : (
                      <>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            {pagedItems.map((item, idx) => (
                              <tr key={item.Id} style={{ borderBottom: '1px solid #f8fafc', background: idx % 2 ? '#fafbfc' : '#fff' }}>
                                <td style={{ width: 28, padding: '2px 4px' }}>
                                  <Checkbox size="small" sx={{ p: 0.15 }}
                                    checked={selectedItems.has(item.Id)}
                                    onChange={(e) => handleSelectItem(item.Id, e.target.checked)} />
                                </td>
                                <td style={{ padding: '2px 6px', fontSize: 10, color: '#94a3b8', width: 24 }}>{idx + 1}</td>
                                <td style={{ padding: '2px 6px', fontSize: 10.5, fontWeight: 500, color: '#1e293b' }}>{item.Name || '-'}</td>
                                <td style={{ padding: '2px 6px', fontSize: 10, color: '#6b7280', width: 60 }}>{item.Code || '-'}</td>
                                <td style={{ padding: '2px 6px', fontSize: 10, color: '#6b7280', width: 70 }}>{item.HSNCode || '-'}</td>
                                <td style={{ padding: '2px 6px', width: 40, textAlign: 'center' }}>
                                  <span style={{ background: '#fef3c7', color: '#92400e', padding: '0px 4px', borderRadius: 6, fontSize: 9, fontWeight: 700 }}>{item.GSTRate}%</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {pagedItems.length < catItems.length && (
                          <div style={{ textAlign: 'center', padding: '4px 0' }}>
                            <button onClick={(e) => { e.stopPropagation(); setCatPages(prev => ({ ...prev, [catId]: page + 1 })); }}
                              style={{ border: 'none', background: '#eef2ff', color: '#4338ca', fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 4, cursor: 'pointer' }}>
                              Load more ({catItems.length - pagedItems.length} remaining)
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Collapse>
              </div>
            );
          })}
        </div>
      ) : oldGstId && !fetching ? (
        <Paper sx={{ p: 2, textAlign: 'center', color: '#6b7280', borderRadius: 2, fontSize: 12 }}>No categories found</Paper>
      ) : !oldGstId ? (
        <Paper sx={{ p: 2, textAlign: 'center', color: '#94a3b8', borderRadius: 2, fontSize: 11 }}>Select Old GST Rate</Paper>
      ) : null}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ fontSize: 11 }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ fontSize: 11 }}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default GSTItemBulkUpdate;
